/* eslint-disable no-unused-vars */
import { FocusEventHandler, ReactNode } from 'react';
import { SxProps, TextFieldVariants } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

export interface ILooseObject {
  [key: string]: any;
}

export interface IPagination {
  page: number;
  size: number;
}

export interface ISorting {
  field: string;
  order: 'asc' | 'desc';
}

export interface IGetApiRequestParams {
  pagination?: IPagination;
  sorting?: ISorting[];
  filters?: ILooseObject;
}

export interface IApiClientResponse {
  headers?: ILooseObject;
  data: ILooseObject;
  error: {
    code: number;
    message: string;
  };
  errors?: ILooseObject;
}

export interface IApiClientInput {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: ILooseObject;
  body?: ILooseObject;
  isCacheable?: boolean;
  // signal?: GenericAbortSignal; //TODO: Implement abort signal
}

export enum DYNAMIC_INPUT_TYPES_ENUM {
  TEXT = 'TEXT',
  SELECT = 'SELECT',
  INTEGER = 'INTEGER',
  DYNAMIC_MULTI_CHIPS_SELECT = 'DYNAMIC_MULTI_CHIPS_SELECT',
  BOOLEAN = 'BOOLEAN',
  MULTI_SELECT = 'MULTI_SELECT',
  DYNAMIC_MULTI_CHIPS_TEXT = 'DYNAMIC_MULTI_CHIPS_TEXT',
  HTML = 'HTML',
  LARGE_TEXT = 'LARGE_TEXT',
  CHECKBOX = 'CHECKBOX',
  MVEL_EDITOR = 'MVEL_EDITOR'
}

export enum INPUT_TYPES_ENUM {
  TEXT = 'text',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  ARRAY = 'array',
  HTML = 'html'
}

export interface IAttributeOptions {
  value: string | boolean;
  label: string;
  details?: string | ReactNode;
}

type UI_TYPE_INPUT_TYPE_MAP_TYPE = {
  [key: string]: DYNAMIC_INPUT_TYPES_ENUM;
};

type INPUT_FIELD_TYPE_MAP_TYPE = {
  [key: string]: INPUT_TYPES_ENUM;
};

export const UI_TYPE_INPUT_TYPE_MAP: UI_TYPE_INPUT_TYPE_MAP_TYPE = {
  STRING: DYNAMIC_INPUT_TYPES_ENUM?.TEXT,
  SELECT: DYNAMIC_INPUT_TYPES_ENUM?.SELECT,
  INTEGER: DYNAMIC_INPUT_TYPES_ENUM?.INTEGER,
  MULTI_SELECT_CHIPS: DYNAMIC_INPUT_TYPES_ENUM?.DYNAMIC_MULTI_CHIPS_SELECT,
  MULTI_SELECT: DYNAMIC_INPUT_TYPES_ENUM?.MULTI_SELECT,
  BOOLEAN: DYNAMIC_INPUT_TYPES_ENUM?.BOOLEAN,
  MULTI_CHIPS_TEXT: DYNAMIC_INPUT_TYPES_ENUM.DYNAMIC_MULTI_CHIPS_TEXT,
  HTML: DYNAMIC_INPUT_TYPES_ENUM?.HTML,
  LARGE_TEXT: DYNAMIC_INPUT_TYPES_ENUM?.LARGE_TEXT,
  CHECKBOX: DYNAMIC_INPUT_TYPES_ENUM?.CHECKBOX
};

export const INPUT_FIELD_TYPE_MAP: INPUT_FIELD_TYPE_MAP_TYPE = {
  TEXT: INPUT_TYPES_ENUM?.TEXT,
  NUMBER: INPUT_TYPES_ENUM?.NUMBER,
  BOOLEAN: INPUT_TYPES_ENUM?.BOOLEAN,
  ARRAY: INPUT_TYPES_ENUM?.ARRAY
};

export interface IDynamicInputProp {
  type: DYNAMIC_INPUT_TYPES_ENUM;
  id: number;
  value: string | string[];
  inputType: INPUT_TYPES_ENUM;
  required?: boolean;
  hasError: boolean;
  name?: string;
  label?: string;
  placeholder?: string;
  options?: IAttributeOptions[];
  helperText?: string;
  fullWidth?: boolean;
  variant?: TextFieldVariants; //'outlined' | 'standard' | 'filled'
  disabled?: boolean;
  sx?: SxProps;
  size?: 'small';
  onChange?: Function;
  onBlur?: FocusEventHandler<HTMLInputElement> | FocusEventHandler<HTMLTextAreaElement>;
  maxLength?: number;
  disableClearable?: boolean;
  loading?: boolean;
  errorValues?: string[];
  richEditorProps?: {
    toolbarOptions?: Array<Array<ILooseObject | string>>;
    formats?: string[];
  };
  maxHeight?: number;
  maxRows?: number;
  maxColumns?: number;
  minWidth?: number;
  chipsSeparator?: string;
  isCheckboxSingleSelect?: boolean;
}

export interface IBreadCrumbsDef {
  key: string | number;
  name: string;
  redirectPath?: string;
}

export interface ListingTableDataStateType {
  columns: GridColDef[];
  rows: ILooseObject[];
}

export interface CredentialAttributeDef {
  key: string;
  label: string;
  valueType: 'text' | 'monospace' | 'date' | 'chips' | 'hidden';
  isSensitive?: boolean;
  showVisibilityToggle?: boolean;
  dateFormat?: {
    year: 'numeric' | '2-digit';
    month: 'long' | 'short' | 'numeric' | '2-digit';
    day: 'numeric' | '2-digit';
    hour?: '2-digit';
    minute?: '2-digit';
  };
  chipColor?: 'primary' | 'secondary' | 'default' | 'error' | 'info' | 'success' | 'warning';
  chipVariant?: 'filled' | 'outlined';
}
